import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/auth';
import { permissionsApi } from '../lib/api';

interface PrivateRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  menuItem?: string;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, allowedRoles, menuItem }) => {
  const { user, isLoading } = useAuth();
  const [hasPermission, setHasPermission] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    let permissionTimeout: NodeJS.Timeout | null = null;

    const checkPermission = async () => {
      // Set a timeout to prevent infinite loading
      permissionTimeout = setTimeout(() => {
        console.warn('Permission check timeout - defaulting to no permission');
        setHasPermission(false);
      }, 5000); // 5 second timeout

      if (user && menuItem) {
        try {
          const permissions = await permissionsApi.getAll();
          const menuPermission = permissions.find(p => p.menu_item === menuItem);

          if (menuPermission) {
            let hasAccess = false;
            switch (user.role) {
              case 'admin':
                hasAccess = menuPermission.admin_access;
                break;
              case 'organizer':
                hasAccess = menuPermission.organizer_access;
                break;
              case 'participant':
                hasAccess = menuPermission.participant_access;
                break;
              case 'pr':
                hasAccess = menuPermission.pr_access;
                break;
            }
            setHasPermission(hasAccess);
          } else {
            setHasPermission(false);
          }
        } catch (error) {
          console.error('Error checking permissions:', error);
          setHasPermission(false);
        } finally {
          if (permissionTimeout) clearTimeout(permissionTimeout);
        }
      } else {
        setHasPermission(true); // No menu item specified, default to allowed
        if (permissionTimeout) clearTimeout(permissionTimeout);
      }
    };

    checkPermission();

    return () => {
      if (permissionTimeout) clearTimeout(permissionTimeout);
    };
  }, [user, menuItem]);

  if (isLoading || hasPermission === null) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  if (!hasPermission) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

export default PrivateRoute;